import { DepartmentRole, Role } from '@21k/shared';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  assignUserDepartment,
  createUser,
  getApiErrorMessage,
  getUser,
  listSystemRoleOptionsForStaff,
  listDepartments,
  removeUserDepartment,
  updateUser,
} from '../../api/client';
import type { DepartmentSummary, UserProfile } from '../../api/types';
import { useAuth } from '../../contexts/AuthContext';
import { canAssignSuperAdmin, canEditStaff, canWriteStaff } from '../../utils/permissions';

const SYSTEM_ROLES: Role[] = [Role.USER, Role.SUPER_ADMIN];

export default function UserFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [systemRoleOptions, setSystemRoleOptions] = useState<
    { id: string; name: string; description: string | null }[]
  >([]);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<Role>(Role.USER);
  const [systemRoleIds, setSystemRoleIds] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [deptToAdd, setDeptToAdd] = useState('');
  const [deptRoleToAdd, setDeptRoleToAdd] = useState<DepartmentRole>(
    DepartmentRole.AGENT,
  );
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSave = currentUser && (isNew ? canWriteStaff(currentUser) : canEditStaff(currentUser));
  const canManageDepartments = currentUser && canEditStaff(currentUser);
  const canPromoteSuperAdmin = currentUser && canAssignSuperAdmin(currentUser);

  useEffect(() => {
    listDepartments().then(setDepartments).catch(() => undefined);
    listSystemRoleOptionsForStaff()
      .then(setSystemRoleOptions)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (isNew || !id) return;
    getUser(id)
      .then((u) => {
        setProfile(u);
        setEmail(u.email);
        setFirstName(u.firstName);
        setLastName(u.lastName);
        setRole(u.role);
        setSystemRoleIds(u.systemRoles.map((sr) => sr.roleId));
        setIsActive(u.isActive);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
        systemRoleIds: role === Role.SUPER_ADMIN ? [] : systemRoleIds,
      };

      if (isNew) {
        const created = await createUser({
          email: email.trim().toLowerCase(),
          ...payload,
        });
        navigate(`/users/${created.id}`);
      } else if (id) {
        const updated = await updateUser(id, {
          ...payload,
          isActive,
        });
        setProfile(updated);
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleAddDepartment() {
    if (!id || isNew || !deptToAdd) return;
    setError('');
    try {
      const updated = await assignUserDepartment(id, deptToAdd, deptRoleToAdd);
      setProfile(updated);
      setDeptToAdd('');
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  }

  async function handleRemoveDepartment(departmentId: string, deptRole: string) {
    if (!id || isNew) return;
    setError('');
    try {
      const updated = await removeUserDepartment(id, departmentId, deptRole);
      setProfile(updated);
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  }

  if (loading) return <div className="loading">Loading…</div>;

  const roleOptions = canPromoteSuperAdmin
    ? SYSTEM_ROLES
    : SYSTEM_ROLES.filter((r) => r !== Role.SUPER_ADMIN);

  return (
    <>
      <div className="page-header">
        <h1>{isNew ? 'Add user' : `${firstName} ${lastName}`}</h1>
        <Link to="/users" className="btn btn-secondary">
          Back
        </Link>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <form className="card-body" onSubmit={handleSave}>
          <div className="form-row">
            {isNew && (
              <div className="form-group">
                <label>Email</label>
                <input
                  className="form-control"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            )}
            <div className="form-group">
              <label>First name</label>
              <input
                className="form-control"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                disabled={!canSave}
              />
            </div>
            <div className="form-group">
              <label>Last name</label>
              <input
                className="form-control"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                disabled={!canSave}
              />
            </div>
            <div className="form-group">
              <label>System role</label>
              <select
                className="form-control"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                disabled={!canSave}
              >
                {roleOptions.map((r) => (
                  <option key={r} value={r}>
                    {r === Role.SUPER_ADMIN ? 'Super Admin' : 'Standard User'}
                  </option>
                ))}
              </select>
              <small style={{ color: 'var(--text-muted)' }}>
                Super Admin has full access to all modules. Only Super Admins can grant this role.
              </small>
            </div>
            {!isNew && (
              <div className="form-group">
                <label>Active</label>
                <select
                  className="form-control"
                  value={isActive ? 'yes' : 'no'}
                  onChange={(e) => setIsActive(e.target.value === 'yes')}
                  disabled={!canSave}
                >
                  <option value="yes">Active</option>
                  <option value="no">Inactive</option>
                </select>
              </div>
            )}
          </div>

          {role !== Role.SUPER_ADMIN && (
            <div className="form-group">
              <label>System roles</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {systemRoleOptions.length === 0 ? (
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    No active roles available.
                  </span>
                ) : (
                  systemRoleOptions.map((r) => (
                    <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={systemRoleIds.includes(r.id)}
                        disabled={!canSave}
                        onChange={(e) => {
                          setSystemRoleIds((prev) =>
                            e.target.checked
                              ? [...prev, r.id]
                              : prev.filter((id) => id !== r.id),
                          );
                        }}
                      />
                      {r.name}
                    </label>
                  ))
                )}
              </div>
              <small style={{ color: 'var(--text-muted)' }}>
                Module permissions are unioned across all assigned roles. Manage roles under Role
                Management.
              </small>
            </div>
          )}

          {canSave && (
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </form>
      </div>

      {!isNew && profile && (
        <div className="card" style={{ marginTop: '1.25rem' }}>
          <div className="card-body">
            <h3 style={{ marginTop: 0 }}>Department memberships</h3>
            {profile.departmentMemberships.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>No department assignments.</p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {profile.departmentMemberships.map((m) => (
                  <li
                    key={m.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.4rem 0',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <span>
                      {m.department.name} — {m.role.replace(/_/g, ' ')}
                    </span>
                    {canManageDepartments && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => void handleRemoveDepartment(m.departmentId, m.role)}
                      >
                        Remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {canManageDepartments && (
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                <select
                  className="form-control"
                  style={{ maxWidth: 220 }}
                  value={deptToAdd}
                  onChange={(e) => setDeptToAdd(e.target.value)}
                >
                  <option value="">Add department…</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
                <select
                  className="form-control"
                  style={{ maxWidth: 180 }}
                  value={deptRoleToAdd}
                  onChange={(e) => setDeptRoleToAdd(e.target.value as DepartmentRole)}
                >
                  {Object.values(DepartmentRole).map((r) => (
                    <option key={r} value={r}>
                      {r.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => void handleAddDepartment()}
                  disabled={!deptToAdd}
                >
                  Assign
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
